/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { v4 as uuid } from 'uuid'

import {
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiText,
  EuiFlexGroup,
  EuiFieldText,
  EuiForm,
  EuiFormRow,
  EuiModalFooter,
  EuiFlexItem,
  EuiButtonEmpty,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { addToast } from '@modules/cui/Toasts'
import { useCreateSaasUserAdminMutation } from '@modules/cloud-lib/users/hooks'
import { ApiErrorCollection } from '@modules/query/types'
import { useConfig } from '@modules/cui/ConfigContext'
import genericMessages from '@modules/mfa-lib/genericMessages'

import SpinButton from '@/components/SpinButton'
import { forgotPasswordUrl } from '@/lib/urlBuilder'
import validateEmail from '@/lib/validateEmail'

import type { FC } from 'react'

export const messages = defineMessages({
  createNewUser: {
    id: 'users.create-user-modal.create-new-user',
    defaultMessage: `Create new user`,
  },
  newEmail: {
    id: 'users.create-user-modal.email-placeholder',
    defaultMessage: `New user's email address`,
  },
  invalidEmail: {
    id: 'users.create-user-modal.email-invalid',
    defaultMessage: `Please enter a valid email address.`,
  },
  failedRequest: {
    id: 'users.create-user-modal.failed-request',
    defaultMessage: `Failed to create user.`,
  },
  userCreated: {
    id: 'users.create-user-modal.user-created',
    defaultMessage: `User created`,
  },
  instructUser: {
    id: 'users.create-user-modal.description-instruct',
    defaultMessage:
      'Please instruct them to navigate to {link} and reset their password to access their account. ',
  },
})

type Props = {
  closeModal: () => void
}

const generateTempPassword = (): string => uuid().replace(/-/g, '').slice(0, 24)

const CreateUserModal: FC<Props> = ({ closeModal }) => {
  const { formatMessage } = useIntl()
  const [emailAddress, setEmailAddress] = useState('')
  const [errors, setErrors] = useState({
    email: '',
    api: '',
  })
  const userConsoleUrl = useConfig('CLOUD_USERCONSOLE_URL')
  const forgotUrl = `${userConsoleUrl}${forgotPasswordUrl()}`

  const { mutate: createSaasUserAdmin, isLoading } = useCreateSaasUserAdminMutation()

  const handleApiError = (err: ApiErrorCollection): string =>
    err.errors?.[0]?.message || formatMessage(messages.failedRequest)

  const handleCreateUser = async () => {
    setErrors({ email: '', api: '' })

    if (!validateEmail(emailAddress)) {
      setErrors((prev) => ({ ...prev, email: formatMessage(messages.invalidEmail) }))
      return
    }

    const tempPassword = generateTempPassword()

    createSaasUserAdmin(
      { email: emailAddress.trim(), password: tempPassword },
      {
        onSuccess: () => {
          addToast({
            title: formatMessage(messages.userCreated),
            color: 'success',
            text: (
              <FormattedMessage
                {...messages.instructUser}
                values={{
                  link: <a href={forgotUrl}>{forgotUrl.replace(/^https?:\/\//, '')}</a>,
                }}
              />
            ),
          })
          closeModal()
        },
        onError: (err: ApiErrorCollection) => {
          setErrors((prev) => ({
            ...prev,
            api: handleApiError(err),
          }))
        },
      },
    )
  }

  return (
    <EuiModal
      onClose={closeModal}
      maxWidth='480px'
      aria-label={formatMessage(messages.createNewUser)}
    >
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          <EuiText>
            <h3>
              <FormattedMessage {...messages.createNewUser} />
            </h3>
          </EuiText>
        </EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiFlexGroup direction='column' gutterSize='l'>
          <EuiText size='s'>
            <p>
              <FormattedMessage
                id='users.create-user-modal.description-email'
                defaultMessage='After you create the user, they will <u>not</u> receive an email. '
                values={{ u: (content) => <u>{content}</u> }}
              />
            </p>
          </EuiText>
          <EuiText size='s'>
            <p>
              <FormattedMessage
                {...messages.instructUser}
                values={{
                  link: <a href={forgotUrl}>{forgotUrl.replace(/^https?:\/\//, '')}</a>,
                }}
              />
            </p>
          </EuiText>
          <EuiForm isInvalid={!!errors.api} error={errors.api}>
            <EuiFormRow isInvalid={!!errors.email} error={errors.email}>
              <EuiFieldText
                placeholder={formatMessage(messages.newEmail)}
                type='email'
                isInvalid={!!errors.email}
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                aria-label={formatMessage(messages.newEmail)}
              />
            </EuiFormRow>
          </EuiForm>
        </EuiFlexGroup>
      </EuiModalBody>
      <EuiModalFooter>
        <EuiFlexGroup justifyContent='flexEnd'>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty onClick={closeModal}>
              <FormattedMessage {...genericMessages.cancel} />
            </EuiButtonEmpty>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <SpinButton
              onClick={handleCreateUser}
              disabled={emailAddress.trim().length === 0 || isLoading}
              fill={true}
              spin={isLoading}
            >
              <FormattedMessage
                id='users.create-user-modal.create-user'
                defaultMessage='Create user'
              />
            </SpinButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiModalFooter>
    </EuiModal>
  )
}

export default CreateUserModal
